import { useState } from "react";
import { toast } from "sonner";
import { CreditCard, Loader2, CheckCircle2, AlertTriangle } from "lucide-react";
import { stripeDiagnostics, formatApiError } from "@/lib/api";

const ROW = "flex flex-wrap items-baseline gap-2 py-1";

export const StripeHealthCard = ({ adminKey }) => {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);

  const run = async () => {
    setBusy(true);
    try {
      setData(await stripeDiagnostics(adminKey));
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const ok = data?.secret_key_kind === "live" && data?.charges_enabled;

  return (
    <div className="border border-steel-border bg-steel-surface p-6" data-testid="stripe-health-card">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="flex h-11 w-11 items-center justify-center border border-steel-border">
            <CreditCard className="h-5 w-5 text-steel-brass" />
          </div>
          <div>
            <h3 className="font-display text-xl font-medium text-steel-text">Card payments</h3>
            <p className="text-xs text-steel-muted">
              Shows which Stripe account THIS server pays into — run it on the live site too.
            </p>
          </div>
        </div>
        <button
          type="button"
          onClick={run}
          disabled={busy}
          data-testid="stripe-health-run-btn"
          className="flex items-center gap-2 border border-steel-border px-5 py-2.5 text-sm font-medium text-steel-secondary transition-colors duration-300 hover:border-steel-brass hover:text-steel-brass disabled:opacity-50"
        >
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <CreditCard className="h-4 w-4" />}
          {busy ? "Checking…" : "Run Stripe check"}
        </button>
      </div>

      {data && (
        <div className="mt-6 space-y-5" data-testid="stripe-health-result">
          <div
            className={`flex items-start gap-3 border p-4 ${
              ok ? "border-emerald-500/50 bg-emerald-950/20" : "border-red-500/50 bg-red-950/20"
            }`}
          >
            {ok ? (
              <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-emerald-400" />
            ) : (
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-red-400" />
            )}
            <p
              className={`text-sm leading-relaxed ${ok ? "text-emerald-300" : "text-red-300"}`}
              data-testid="stripe-health-verdict"
            >
              {data.verdict}
            </p>
          </div>
          <div className="border border-steel-border bg-steel-elevated p-4">
            <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-steel-muted">Configuration</p>
            <div className="mt-2 font-mono text-xs text-steel-secondary">
              {Object.entries(data)
                .filter(([k]) => k !== "verdict")
                .map(([k, v]) => (
                  <div key={k} className={ROW}>
                    <span className="text-steel-muted">{k}</span>
                    <span className="text-steel-text">{String(v)}</span>
                  </div>
                ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default StripeHealthCard;
